export type ScreenplayLineKind =
  | "heading"
  | "action"
  | "character"
  | "parenthetical"
  | "dialogue";

export type ScriptEditorDemoDensity = "hero" | "showcase";

/** Hero home pipeline: flexible columns so the screenplay stays visible on narrow viewports. */
export const SCRIPT_EDITOR_DEMO_SCENE_NAV_CLASS =
  "flex h-full w-[24%] min-w-[84px] max-w-[200px] min-h-0 shrink flex-col self-stretch overflow-hidden border-r border-white/[0.05] bg-[#0F1117] md:max-w-[216px]";

export const SCRIPT_EDITOR_DEMO_INSPECTOR_CLASS =
  "flex w-[24%] min-w-[72px] max-w-[200px] min-h-0 shrink flex-col self-stretch overflow-hidden border-l border-white/[0.05] bg-[#12141a] text-left md:max-w-[216px]";

export const SCRIPT_EDITOR_DEMO_PAPER_SHEET_CLASS =
  "relative flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-sm border border-stone-300/70 bg-[rgb(255,252,246)] shadow-lg dark:border-white/[0.05] dark:bg-[rgb(255,252,246)] dark:shadow-[0_20px_50px_-18px_rgba(0,0,0,0.65)]";

export const SCRIPT_EDITOR_DEMO_PAPER_FRAME_CLASS =
  "relative flex h-full min-h-0 min-w-0 flex-[1.6] basis-0 items-stretch overflow-hidden bg-[#0F1117] p-1.5 sm:p-2 md:p-3";

export const SCRIPT_EDITOR_DEMO_SCREENPLAY_CLASS =
  "font-['Courier_New',Courier,monospace] text-[9px] leading-[1.45] sm:text-[10px]";

/**
 * Feature showcase preview: wider paper, larger type, tighter side rails.
 * `app-demo-scene-nav` and `app-demo-inspector` mark the side rails so a
 * narrow container can hide them (see home-prep.css, `.prep-app-view`).
 * They do nothing outside that container.
 */
export const SCRIPT_EDITOR_DEMO_SHOWCASE_SCENE_NAV_CLASS =
  "app-demo-scene-nav flex h-full w-[132px] min-h-0 shrink-0 flex-col self-stretch overflow-hidden border-r border-white/[0.05] bg-[#0F1117] sm:w-[148px] lg:w-[156px]";

export const SCRIPT_EDITOR_DEMO_SHOWCASE_INSPECTOR_CLASS =
  "app-demo-inspector flex w-[132px] shrink-0 flex-col self-stretch overflow-hidden border-l border-white/[0.05] bg-[#12141a] text-left sm:w-[148px] lg:w-[156px]";

export const SCRIPT_EDITOR_DEMO_SHOWCASE_PAPER_SHEET_CLASS =
  "relative flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-sm border border-stone-300/70 bg-[rgb(255,252,246)] shadow-lg dark:border-white/[0.05] dark:bg-[rgb(255,252,246)] dark:shadow-[0_20px_50px_-18px_rgba(0,0,0,0.65)]";

export const SCRIPT_EDITOR_DEMO_SHOWCASE_PAPER_FRAME_CLASS =
  "relative flex h-full min-h-0 min-w-0 flex-1 items-stretch overflow-hidden bg-[#0F1117] p-1 sm:p-1.5";

export const SCRIPT_EDITOR_DEMO_SHOWCASE_SCREENPLAY_CLASS =
  "font-['Courier_New',Courier,monospace] text-[10.5px] leading-[1.45] sm:text-[11px] lg:text-[12px]";

export type ScriptEditorDemoLayout = {
  sceneNavClass: string;
  inspectorClass: string;
  paperSheetClass: string;
  paperFrameClass: string;
  screenplayClass: string;
};

export const SCRIPT_EDITOR_DEMO_LAYOUT: Record<
  ScriptEditorDemoDensity,
  ScriptEditorDemoLayout
> = {
  hero: {
    sceneNavClass: SCRIPT_EDITOR_DEMO_SCENE_NAV_CLASS,
    inspectorClass: SCRIPT_EDITOR_DEMO_INSPECTOR_CLASS,
    paperSheetClass: SCRIPT_EDITOR_DEMO_PAPER_SHEET_CLASS,
    paperFrameClass: SCRIPT_EDITOR_DEMO_PAPER_FRAME_CLASS,
    screenplayClass: SCRIPT_EDITOR_DEMO_SCREENPLAY_CLASS,
  },
  showcase: {
    sceneNavClass: SCRIPT_EDITOR_DEMO_SHOWCASE_SCENE_NAV_CLASS,
    inspectorClass: SCRIPT_EDITOR_DEMO_SHOWCASE_INSPECTOR_CLASS,
    paperSheetClass: SCRIPT_EDITOR_DEMO_SHOWCASE_PAPER_SHEET_CLASS,
    paperFrameClass: SCRIPT_EDITOR_DEMO_SHOWCASE_PAPER_FRAME_CLASS,
    screenplayClass: SCRIPT_EDITOR_DEMO_SHOWCASE_SCREENPLAY_CLASS,
  },
};

export type ScreenplayLine = {
  id: string;
  kind: ScreenplayLineKind;
  text: string;
  typeSpeed?: number;
  pauseAfter?: number;
  pageNumber?: number;
};

export type DemoScene = {
  id: number;
  heading: string;
  page: string;
  isActive?: boolean;
  hasOpenComment?: boolean;
};

export const DEMO_SCENES: DemoScene[] = [
  { id: 14, heading: "EXT. RIVER BANK - DAWN", page: "14" },
  { id: 13, heading: "INT. CATWALK - CONTINUOUS", page: "13" },
  {
    id: 12,
    heading: "INT. WAREHOUSE - NIGHT",
    page: "12",
    isActive: true,
    hasOpenComment: true,
  },
  { id: 11, heading: "EXT. ALLEY - DAY", page: "11" },
  { id: 10, heading: "EXT. DOCKS - NIGHT", page: "10" },
  { id: 9, heading: "INT. MARCUS APT. - DAY", page: "9" },
  { id: 8, heading: "EXT. ROOFTOP - NIGHT", page: "8" },
  { id: 7, heading: "INT. OFFICE - DAY", page: "7" },
  { id: 6, heading: "INT. POLICE STATION - DAY", page: "6" },
  { id: 5, heading: "INT. LOBBY - DAY", page: "5" },
  { id: 4, heading: "EXT. LOT - SUNSET", page: "4" },
  { id: 3, heading: "EXT. HIGHWAY - DUSK", page: "3" },
  { id: 2, heading: "EXT. GARAGE - NIGHT", page: "2" },
  { id: 1, heading: "INT. HOSPITAL ROOM - DAY", page: "1" },
];

export const DEMO_SCENE_INITIAL_ORDER = [
  14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1,
] as const;

/** Alley moves above warehouse: mirrors scripted scene reorder in the nav. */
export const DEMO_SCENE_REORDERED = [
  11, 12, 14, 13, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1,
] as const;

/** Warehouse scene (12): prefilled on the page when typing begins. Each INT./EXT. heading is its own scene. */
export const DEMO_PREFILLED_LINES: ScreenplayLine[] = [
  {
    id: "scene-warehouse",
    kind: "heading",
    text: "INT. WAREHOUSE - NIGHT",
    pageNumber: 12,
  },
  {
    id: "warehouse-action-1",
    kind: "action",
    text: "A cavernous space. Rows of crates disappear into shadow. Rain hammers the corrugated roof.",
  },
  {
    id: "warehouse-action-1b",
    kind: "action",
    text: "Water drips through rusted skylights. Marcus sweeps his flashlight across stacked pallets and faded shipping labels.",
  },
  {
    id: "warehouse-action-2",
    kind: "action",
    text: "Marcus moves between stacks, flashlight low. He freezes, FOOTSTEPS echo from the loading bay.",
  },
  {
    id: "warehouse-action-3",
    kind: "action",
    text: "He kills the light. Listens. Somewhere above, metal groans, old CATWALK rigging shifting under weight.",
  },
  { id: "marcus", kind: "character", text: "MARCUS" },
  { id: "parenthetical", kind: "parenthetical", text: "(under his breath)" },
  { id: "dialogue-1", kind: "dialogue", text: "We shouldn't be here." },
  {
    id: "warehouse-action-4",
    kind: "action",
    text: "Nothing. Then a red laser dot settles on his chest. He slowly raises his hands.",
  },
];

export type DemoInlineTagHighlight = {
  tagId: string;
  match: string;
  showTagPopover?: boolean;
};

export const DEMO_PREFILLED_LINE_TAGS: Record<
  string,
  DemoInlineTagHighlight[]
> = {
  "warehouse-action-1": [{ tagId: "rain", match: "Rain" }],
  "warehouse-action-1b": [{ tagId: "flashlight", match: "flashlight" }],
  "warehouse-action-2": [
    { tagId: "flashlight", match: "flashlight" },
    { tagId: "footsteps", match: "FOOTSTEPS", showTagPopover: true },
  ],
  "warehouse-action-3": [{ tagId: "catwalk", match: "CATWALK" }],
  marcus: [{ tagId: "marcus", match: "MARCUS" }],
};

/** Continues the Marcus scene: typed out slowly. */
export const DEMO_ANIMATED_LINES: ScreenplayLine[] = [
  { id: "elena", kind: "character", text: "ELENA (O.S.)", pauseAfter: 500 },
  {
    id: "elena-paren",
    kind: "parenthetical",
    text: "(from the catwalk)",
    pauseAfter: 550,
  },
  {
    id: "elena-dialogue",
    kind: "dialogue",
    text: "Marcus. Don't move.",
    typeSpeed: 118,
    pauseAfter: 1600,
  },
  {
    id: "whips-action",
    kind: "action",
    text: "Marcus whips around. On the catwalk, Elena lowers into view, handgun drawn.",
    typeSpeed: 78,
    pauseAfter: 1100,
  },
  { id: "marcus-2", kind: "character", text: "MARCUS", pauseAfter: 550 },
  {
    id: "dialogue-2",
    kind: "dialogue",
    text: "We need to move. Now.",
    typeSpeed: 112,
    pauseAfter: 1400,
  },
];

export const DEMO_TAG_CATEGORY_IDS = ["cast", "sound", "props", "vfx"] as const;

/** @deprecated Use buildDemoTagCategories from home-demo-content */
export const DEMO_TAG_CATEGORIES = [
  { color: "#FFC539", label: "Cast" },
  { color: "#5EA8FF", label: "Sound" },
  { color: "#A78BFA", label: "Props" },
  { color: "#34D399", label: "VFX" },
] as const;

export type DemoAppliedTag = {
  id: string;
  label: string;
  category: string;
  color: string;
  subtitle?: string;
};

export type DemoAppliedTagStructure = {
  id: string;
  label: string;
  categoryId: (typeof DEMO_TAG_CATEGORY_IDS)[number] | "set";
  color: string;
  subtitleKey?: "characterOnePlace" | "actionOnePlace" | "actionTwoPlaces";
};

export const DEMO_APPLIED_TAG_STRUCTURES: DemoAppliedTagStructure[] = [
  {
    id: "marcus",
    label: "MARCUS",
    categoryId: "cast",
    color: "#FFC539",
    subtitleKey: "characterOnePlace",
  },
  {
    id: "rain",
    label: "RAIN",
    categoryId: "vfx",
    color: "#34D399",
    subtitleKey: "actionOnePlace",
  },
  {
    id: "flashlight",
    label: "FLASHLIGHT",
    categoryId: "props",
    color: "#A78BFA",
    subtitleKey: "actionTwoPlaces",
  },
  {
    id: "catwalk",
    label: "CATWALK",
    categoryId: "set",
    color: "#64748B",
    subtitleKey: "actionOnePlace",
  },
  {
    id: "footsteps",
    label: "FOOTSTEPS",
    categoryId: "sound",
    color: "#5EA8FF",
    subtitleKey: "actionOnePlace",
  },
];

/** Applied tags shown in the inspector rail (matches script-editor AppliedTagsSection). */
export const DEMO_APPLIED_TAGS: DemoAppliedTag[] =
  DEMO_APPLIED_TAG_STRUCTURES.map((tag) => ({
    id: tag.id,
    label: tag.label,
    category:
      tag.categoryId === "set"
        ? "Set"
        : tag.categoryId.charAt(0).toUpperCase() + tag.categoryId.slice(1),
    color: tag.color,
    subtitle:
      tag.subtitleKey === "characterOnePlace"
        ? "Character · 1 place"
        : tag.subtitleKey === "actionTwoPlaces"
          ? "Action · 2 places"
          : tag.subtitleKey
            ? "Action · 1 place"
            : undefined,
  }));

export type DemoScriptComment = {
  id: string;
  authorName: string;
  authorInitials: string;
  body: string;
  anchorQuote: string;
  status: "open" | "resolved";
};

export const DEMO_SCRIPT_COMMENT: DemoScriptComment = {
  id: "elena-dialogue-2",
  authorName: "Elena Park",
  authorInitials: "EP",
  body: "She just told him not to move, can we hold a beat before he pushes to run?",
  anchorQuote: "We need to move. Now.",
  status: "open",
};

/** @deprecated Use DEMO_PREFILLED_LINES + DEMO_ANIMATED_LINES */
export const DEMO_SCREENPLAY_LINES: ScreenplayLine[] = [
  ...DEMO_PREFILLED_LINES,
  ...DEMO_ANIMATED_LINES,
];
